<template>
  <v-row>
    <v-col cols="12">
      <v-card class="dn-card" flat>
        <div class="dn-header pl-base-bg">
          <v-icon color="#fff" size="18" class="mr-3">mdi-file-document-plus-outline</v-icon>
          <div>
            <div class="dn-title">Edit Debit Note</div>
            <div class="dn-subtitle">{{ form.debitNoteNumber ? `Debit Note #${form.debitNoteNumber}` : "Update debit note details" }}</div>
          </div>
          <v-spacer />
          <v-chip :color="statusColor(status)" dark small class="text-uppercase font-weight-bold">
            {{ status }}
          </v-chip>
        </div>

        <v-alert v-if="refundRecorded" type="info" outlined dense border="left" class="mx-6 mt-4">
          This debit note's refund has already been recorded and can no longer be edited.
        </v-alert>

        <v-form ref="form" v-model="valid" class="pa-6" :disabled="isLocked">
          <div class="dn-section-label">Debit Note Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.debitNoteNumber"
                label="Debit Note Number"
                outlined
                dense
                :disabled="isLocked"
                :rules="[rules.required, rules.uniqueDebitNote]"
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.debitNoteDate"
                    label="Debit Note Date"
                    outlined
                    dense
                    readonly
                    :disabled="isLocked"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.debitNoteDate" @input="dateMenu = false" />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="4">
              <v-select
                v-model="form.reason"
                :items="reasonOptions"
                label="Reason"
                outlined
                dense
                :disabled="isLocked"
              />
            </v-col>
          </v-row>

          <div class="dn-purchase-summary">
            <div class="dn-purchase-summary-row">
              <span>Against Bill</span>
              <span>#{{ billNumber }}</span>
            </div>
            <div class="dn-purchase-summary-row">
              <span>Vendor</span>
              <span>{{ vendorName }}</span>
            </div>
          </div>

          <div class="dn-section-label mt-4">Items</div>

          <div
            v-for="(item, index) in form.items"
            :key="index"
            class="dn-item-row"
          >
            <div class="dn-item-row-head">
              <span class="dn-item-index">Item {{ index + 1 }}</span>
              <v-btn icon x-small class="dn-item-remove" :disabled="isLocked" @click="removeItem(index)">
                <v-icon color="error" size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="item.description"
                  label="Description"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="6" sm="2">
                <v-text-field v-model="item.hsn" label="HSN/SAC" dense outlined hide-details :disabled="isLocked" />
              </v-col>

              <v-col cols="4" sm="1">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Qty"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.price"
                  type="number"
                  label="Rate"
                  dense
                  outlined
                  hide-details
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.taxPercent"
                  type="number"
                  label="Tax %"
                  dense
                  outlined
                  hide-details
                  suffix="%"
                  :disabled="isLocked"
                  @input="calcItem(index)"
                />
              </v-col>
            </v-row>

            <div class="dn-item-row-foot">
              <div class="dn-item-amount">
                <span class="dn-item-amount-label">Amount</span>
                <span class="dn-item-amount-value">{{ formatAmount(item.amount) }}</span>
              </div>

              <div class="dn-item-itc" :class="{ 'dn-item-itc--off': !item.isItcEligible, 'dn-item-itc--locked': isCancelled }">
                <v-switch
                  v-model="item.isItcEligible"
                  dense
                  inset
                  hide-details
                  :disabled="isLocked"
                  class="dn-item-itc-switch"
                />
                <span>ITC Eligible</span>
              </div>
            </div>
          </div>

          <v-btn small class="dn-add-item-btn" :disabled="isLocked" @click="addItem">
            <v-icon left size="16">mdi-plus</v-icon>
            Add Item
          </v-btn>

          <v-divider class="my-4" />
          <v-row dense justify="end">
            <v-col cols="12" sm="4">
              <div class="dn-total-row">
                <span>Subtotal</span>
                <span>{{ formatAmount(subtotal) }}</span>
              </div>
              <div class="dn-total-row">
                <span>GST</span>
                <span>{{ formatAmount(totalGst) }}</span>
              </div>
              <div class="dn-total-row dn-total-row--grand">
                <span>Total</span>
                <span>{{ formatAmount(subtotal + totalGst) }}</span>
              </div>
            </v-col>
          </v-row>

          <v-alert v-if="!isLocked && isZeroAmount" type="warning" outlined dense class="mt-2">
            Debit note total must be greater than ₹0 — enter a quantity and rate for at least one item.
          </v-alert>

          <v-textarea v-model="form.note" label="Note" outlined dense rows="2" :disabled="isLocked" />

          <v-row class="mt-4" justify="end" dense>
            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block color="error" outlined :disabled="isLocked" @click="confirmDelete = true">
                Delete
              </v-btn>
            </v-col>

            <v-col cols="4" sm="auto">
              <v-btn block color="secondary" @click="fetchDebitNote">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="4" sm="auto">
              <v-btn block class="dn-save-btn" :loading="loading" :disabled="isLocked || isZeroAmount" @click="submit">
                Update
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <!-- DELETE -->
    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="deleteDialogButtons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this debit note?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "EditDebitNote",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      dateMenu: false,
      status: "ISSUED",
      billNumber: "",
      vendorName: "",
      transactionId: null,
      confirmDelete: false,
      deleting: false,

      reasonOptions: [
        { text: "Purchase Return", value: "PURCHASE_RETURN" },
        { text: "Price Correction", value: "PRICE_CORRECTION" },
        { text: "Other", value: "OTHER" },
      ],

      form: {
        debitNoteNumber: "",
        debitNoteDate: "",
        reason: "PURCHASE_RETURN",
        note: "",
        items: [],
      },

      rules: {
        required: (v) => !!v || "Required",
        uniqueDebitNote: (v) => {
          if (!v || !this.debitNotes.length) return true;
          const id = this.$route.params.id;
          return (
            !this.debitNotes.some(
              (d) => d._id !== id && d.debitNoteNumber?.toLowerCase() === v.toLowerCase(),
            ) || "Debit note number already exists"
          );
        },
      },
    };
  },

  created() {
    this.$store.dispatch("debitNotes/getDebitNotes");
    this.fetchDebitNote();
  },

  computed: {
    isCancelled() {
      return this.status === "CANCELLED";
    },

    refundRecorded() {
      return !!this.transactionId;
    },

    isLocked() {
      return this.isCancelled || this.refundRecorded;
    },

    debitNotes() {
      return this.$store.state.debitNotes.list || [];
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    totalGst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Number(it.taxPercent) || 0;
        return sum + (amount * percent) / 100;
      }, 0);
    },

    isZeroAmount() {
      return this.subtotal + this.totalGst <= 0.01;
    },

    deleteDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Delete",
          action: this.deleteDebitNote,
          color: "error",
          loading: this.deleting,
        },
      ];
    },
  },

  methods: {
    async fetchDebitNote() {
      try {
        const id = this.$route.params.id;
        const res = await this.$store.dispatch("debitNotes/getDebitNoteById", id);
        const dn = res.debitNote;

        this.status = dn.status;
        this.billNumber = dn.billNumber;
        this.vendorName = dn.vendor?.name || "-";
        this.transactionId = dn.transactionId || null;

        this.form = {
          debitNoteNumber: dn.debitNoteNumber || "",
          debitNoteDate: dn.debitNoteDate ? new Date(dn.debitNoteDate).toISOString().slice(0, 10) : "",
          reason: dn.reason || "PURCHASE_RETURN",
          note: dn.note || "",
          items: (dn.items || []).map((it) => ({ ...it })),
        };
      } catch (err) {
        this.$toast?.error("Failed to load debit note");
      }
    },

    addItem() {
      this.form.items.push({
        description: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
        isItcEligible: true,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    statusColor(status) {
      switch (status) {
        case "ISSUED":
          return "green";
        case "CANCELLED":
          return "red";
        default:
          return "grey";
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;

      try {
        const id = this.$route.params.id;
        const payload = {
          id,
          debitNoteNumber: this.form.debitNoteNumber,
          debitNoteDate: this.form.debitNoteDate,
          reason: this.form.reason,
          items: this.form.items,
          note: this.form.note,
        };

        await this.$store.dispatch("debitNotes/updateDebitNote", payload);

        this.$toast?.success("Debit note updated successfully");
        this.$router.push(`/debit-notes/${id}/view`);
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to update debit note",
        );
      } finally {
        this.loading = false;
      }
    },

    async deleteDebitNote() {
      this.deleting = true;
      try {
        await this.$store.dispatch("debitNotes/deleteDebitNote", this.$route.params.id);
        this.$toast?.success("Debit note deleted");
        this.$router.push("/debit-notes");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to delete debit note",
        );
      } finally {
        this.deleting = false;
        this.confirmDelete = false;
      }
    },
  },
};
</script>

<style scoped>
.dn-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.dn-header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
}

.dn-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.dn-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.dn-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.dn-purchase-summary {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 14px 16px;
  margin-bottom: 4px;
}

.dn-purchase-summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 13.5px;
  color: #475569;
  padding: 3px 0;
}

.dn-item-row {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
}

.dn-item-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.dn-item-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.dn-item-remove {
  margin: -4px -4px 0 0;
}

.dn-item-row-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.dn-item-amount {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.dn-item-amount-label {
  font-size: 12px;
  color: #64748b;
}

.dn-item-amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.dn-item-itc {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
  background: #e6f7ec;
  border-radius: 100px;
  padding: 4px 12px 4px 4px;
  transition: background 0.15s, color 0.15s;
}

.dn-item-itc--off {
  color: #94a3b8;
  background: #eef1f5;
}

.dn-item-itc--locked {
  opacity: 0.6;
}

.dn-item-itc-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.dn-item-itc-switch >>> .v-input__control,
.dn-item-itc-switch >>> .v-input__slot {
  margin: 0 !important;
}

.dn-item-itc-switch >>> .v-messages {
  display: none;
}

.dn-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.dn-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
  color: #475569;
}

.dn-total-row--grand {
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  margin-top: 4px;
  padding-top: 8px;
}

.dn-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
